import { useState } from 'react';
import { Link } from 'react-router-dom';
import type { Invoice, PlanWithFeatures } from '@neuroedge/shared';
import { Button, Card, Modal, PageState, TextField, useToast } from '../../components/ui';
import { useAuth } from '../../auth/AuthContext';
import {
  useMySubscription,
  useMyInvoices,
  usePlansWithFeatures,
  usePlanChangeRequests,
  useCreatePlanChangeRequest,
  usePayInvoice,
  usePaymentConfig,
  useUpgradePlan,
  useDownloadInvoiceDocument,
  type InvoiceDocumentKind,
} from '../../api/billing';
import { MyLimitsCard } from './LimitsCards';
import { PlanCard, PlanGrid, money } from '../../components/billing/PlanCard';

/** Paid / Overdue / Due / Cancelled - from the status and due date. */
function invoiceBadge(inv: Invoice): { label: string; tone?: string } {
  if (inv.status === 'PAID') return { label: 'Paid', tone: 'var(--color-success)' };
  if (inv.status === 'VOID') return { label: 'Cancelled', tone: 'var(--color-text-muted)' };
  if (inv.dueAt && new Date(inv.dueAt).getTime() < Date.now()) {
    return { label: 'Overdue', tone: 'var(--color-danger)' };
  }
  return { label: 'Due' };
}

/**
 * Invoices with their GST breakdown and status; owners/admins pay an unpaid
 * one with Razorpay Checkout ("Pay now"). The amount always comes from the
 * invoice on our server.
 */
function InvoicesCard() {
  const { hasPermission } = useAuth();
  const toast = useToast();
  const canPay = hasPermission('billing.manage');
  const invoices = useMyInvoices({ pageSize: 50 });
  const pay = usePayInvoice();
  const download = useDownloadInvoiceDocument();

  async function onDownload(inv: Invoice, kind: InvoiceDocumentKind) {
    try {
      await download.mutateAsync({ invoiceId: inv.id, kind });
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'Could not download the document.');
    }
  }

  async function onPay(inv: Invoice) {
    try {
      const paid = await pay.mutateAsync(inv.id);
      if (paid)
        toast.success(`Payment received for ${paid.invoiceNumber ?? 'the invoice'}. Thank you!`);
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'The payment did not go through.');
    }
  }

  return (
    <Card title="Invoices">
      {invoices.isPending ? (
        <PageState kind="loading" message="Loading invoices…" />
      ) : invoices.isError ? (
        <PageState kind="error" message={invoices.error.message} />
      ) : invoices.data.items.length === 0 ? (
        <PageState kind="empty" title="No invoices yet" />
      ) : (
        <div style={{ overflowX: 'auto' }}>
          <table className="ne-table" style={{ minWidth: 640 }}>
            <thead>
              <tr>
                <th>Invoice</th>
                <th>Period</th>
                <th style={{ textAlign: 'right' }}>Amount</th>
                <th>Status</th>
                <th aria-label="Actions" />
              </tr>
            </thead>
            <tbody>
              {invoices.data.items.map((inv) => {
                const badge = invoiceBadge(inv);
                const paying = pay.isPending && pay.variables === inv.id;
                return (
                  <tr key={inv.id} style={{ cursor: 'default', verticalAlign: 'top' }}>
                    <td>
                      <strong>{inv.invoiceNumber ?? '—'}</strong>
                      <div
                        style={{
                          color: 'var(--color-text-muted)',
                          fontSize: 'var(--font-size-sm)',
                        }}
                      >
                        {inv.planName} · issued {new Date(inv.issuedAt).toLocaleDateString()}
                      </div>
                    </td>
                    <td>
                      {inv.periodStart} → {inv.periodEnd}
                    </td>
                    <td style={{ textAlign: 'right' }}>
                      <strong>{money(inv.totalAmount, inv.currency)}</strong>
                      {inv.taxAmount > 0 ? (
                        <div
                          style={{
                            color: 'var(--color-text-muted)',
                            fontSize: 'var(--font-size-sm)',
                          }}
                        >
                          {money(inv.subtotalAmount, inv.currency)} +{' '}
                          {inv.igstAmount > 0
                            ? `IGST ${inv.taxRate}% ${money(inv.igstAmount, inv.currency)}`
                            : `CGST ${money(inv.cgstAmount, inv.currency)} + SGST ${money(inv.sgstAmount, inv.currency)}`}
                        </div>
                      ) : null}
                    </td>
                    <td>
                      <span className="ne-badge" style={{ color: badge.tone }}>
                        {badge.label}
                      </span>
                      <div
                        style={{
                          color: 'var(--color-text-muted)',
                          fontSize: 'var(--font-size-sm)',
                        }}
                      >
                        {inv.status === 'PAID' && inv.paidAt
                          ? `Paid ${new Date(inv.paidAt).toLocaleDateString()}`
                          : inv.status === 'ISSUED' && inv.dueAt
                            ? `Due ${new Date(inv.dueAt).toLocaleDateString()}`
                            : null}
                      </div>
                    </td>
                    <td>
                      <div style={{ display: 'flex', flexWrap: 'wrap', gap: 'var(--space-1)' }}>
                        {canPay && inv.status === 'ISSUED' ? (
                          <Button
                            disabled={pay.isPending}
                            aria-label={`Pay ${inv.invoiceNumber ?? 'invoice'} now`}
                            onClick={() => void onPay(inv)}
                          >
                            {paying ? 'Opening…' : 'Pay now'}
                          </Button>
                        ) : null}
                        <Button
                          variant="secondary"
                          disabled={download.isPending}
                          aria-label={`Download invoice ${inv.invoiceNumber ?? ''}`.trim()}
                          onClick={() => void onDownload(inv, 'pdf')}
                        >
                          Invoice
                        </Button>
                        {inv.status === 'PAID' ? (
                          <Button
                            variant="secondary"
                            disabled={download.isPending}
                            aria-label={`Download receipt ${inv.invoiceNumber ?? ''}`.trim()}
                            onClick={() => void onDownload(inv, 'receipt')}
                          >
                            Receipt
                          </Button>
                        ) : null}
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </Card>
  );
}

function when(iso: string): string {
  return new Date(iso).toLocaleString();
}

const AI_DOC_PACKS = [
  { docs: 50, price: '₹499' },
  { docs: 150, price: '₹1,299' },
  { docs: 400, price: '₹2,999' },
];

const ONE_OFF_ADD_ONS = [
  { label: 'Extra team user', price: '₹199/mo' },
  { label: 'Extra WhatsApp number', price: '₹499/mo' },
  { label: 'Onboarding & setup assistance', price: '₹2,999 one-time' },
  { label: 'Custom automation flow', price: 'Starting ₹4,999 one-time' },
];

function UpgradeModal({ plan, onClose }: { plan: PlanWithFeatures; onClose: () => void }) {
  const create = useCreatePlanChangeRequest();
  const toast = useToast();
  const [note, setNote] = useState('');
  const [error, setError] = useState<string | null>(null);

  async function onSubmit() {
    setError(null);
    try {
      await create.mutateAsync({ targetPlanId: plan.id, note: note.trim() || undefined });
      toast.success('Upgrade request submitted — a platform admin will review it shortly.');
      onClose();
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Could not submit the request.';
      setError(message);
      toast.error(message);
    }
  }

  return (
    <Modal title={`Request upgrade to ${plan.name}`} onClose={onClose}>
      <div style={{ display: 'grid', gap: 'var(--space-3)' }}>
        <p style={{ margin: 0, color: 'var(--color-text-muted)' }}>
          This sends a request to a platform admin — your plan won't change until it's approved.
        </p>
        <TextField
          label="Note (optional)"
          placeholder="Why do you need this plan?"
          value={note}
          onChange={(e) => setNote(e.target.value)}
        />
        {error ? <p style={{ margin: 0, color: 'var(--color-danger)' }}>{error}</p> : null}
        <div style={{ display: 'flex', gap: 'var(--space-2)' }}>
          <Button disabled={create.isPending} onClick={() => void onSubmit()}>
            {create.isPending ? 'Submitting…' : 'Submit request'}
          </Button>
          <Button variant="secondary" onClick={onClose}>
            Cancel
          </Button>
        </div>
      </div>
    </Modal>
  );
}

/**
 * Confirms a self-serve plan change before Checkout opens: one month of the
 * plan from today, the plan switches once the payment is confirmed.
 */
function PayPlanModal({
  plan,
  activating,
  onClose,
}: {
  plan: PlanWithFeatures;
  activating: boolean;
  onClose: () => void;
}) {
  const upgrade = useUpgradePlan();
  const { refreshMe } = useAuth();
  const toast = useToast();
  const [error, setError] = useState<string | null>(null);
  const nextRenewal = new Date();
  nextRenewal.setMonth(nextRenewal.getMonth() + 1);

  async function onPay() {
    setError(null);
    try {
      const paid = await upgrade.mutateAsync(plan.id);
      if (paid) {
        // The new plan's menus show up in the navigation right away.
        await refreshMe().catch(() => undefined);
        toast.success(`Payment received - you're now on ${plan.name}.`);
        onClose();
      }
    } catch (err) {
      const message = err instanceof Error ? err.message : 'The payment did not go through.';
      setError(message);
      toast.error(message);
    }
  }

  return (
    <Modal
      title={activating ? `Activate ${plan.name}` : `Upgrade to ${plan.name}`}
      onClose={onClose}
    >
      <div style={{ display: 'grid', gap: 'var(--space-3)' }}>
        <p style={{ margin: 0 }}>
          You&apos;ll pay <strong>{money(plan.monthlyFeeAmount, plan.currency)}</strong> (plus GST
          where applicable) for one month of {plan.name}, starting today.
        </p>
        <p style={{ margin: 0, color: 'var(--color-text-muted)' }}>
          Your plan switches as soon as the payment is confirmed. The next renewal is due around{' '}
          {nextRenewal.toLocaleDateString()}. If you close the payment window, the invoice stays
          under Invoices and you can pay it later.
        </p>
        {error ? <p style={{ margin: 0, color: 'var(--color-danger)' }}>{error}</p> : null}
        <div style={{ display: 'flex', gap: 'var(--space-2)' }}>
          <Button disabled={upgrade.isPending} onClick={() => void onPay()}>
            {upgrade.isPending
              ? 'Opening payment…'
              : activating
                ? 'Pay & activate'
                : 'Pay & upgrade'}
          </Button>
          <Button variant="secondary" onClick={onClose}>
            Cancel
          </Button>
        </div>
      </div>
    </Modal>
  );
}

function PlanTile({
  plan,
  isCurrent,
  canRequestUpgrade,
  onRequestUpgrade,
  payAction,
  onPay,
}: {
  plan: PlanWithFeatures;
  isCurrent: boolean;
  canRequestUpgrade: boolean;
  onRequestUpgrade: () => void;
  /** Online self-serve action for this tile, if any. */
  payAction: 'upgrade' | 'activate' | null;
  onPay: () => void;
}) {
  return (
    <PlanCard plan={plan} highlighted={isCurrent}>
      {isCurrent ? (
        <div
          style={{ display: 'flex', gap: 'var(--space-2)', alignItems: 'center', flexWrap: 'wrap' }}
        >
          <span className="ne-badge">Current plan</span>
          {payAction === 'activate' ? (
            <Button type="button" onClick={onPay}>
              Activate now
            </Button>
          ) : null}
        </div>
      ) : plan.isCustomPricing ? (
        <p style={{ margin: 0, color: 'var(--color-text-muted)' }}>Contact us for pricing.</p>
      ) : payAction === 'upgrade' ? (
        <Button type="button" onClick={onPay}>
          Upgrade now
        </Button>
      ) : canRequestUpgrade ? (
        <Button type="button" onClick={onRequestUpgrade}>
          Request upgrade
        </Button>
      ) : null}
    </PlanCard>
  );
}

/**
 * Tenant's own billing view (`billing.read`): the pricing catalogue as tiles
 * (current plan marked) and invoice history. With online payments on,
 * owners/admins switch to a higher plan - or activate their trial plan - by
 * paying for it ("Upgrade now"; the plan switches once paid, ADR-032 phase 2).
 * Otherwise, and for moving to a cheaper plan, "Request upgrade" opens an
 * admin-reviewed request. Enterprise is "Contact us" only.
 */
export function BillingPage() {
  const { hasPermission } = useAuth();
  const subscription = useMySubscription();
  const plans = usePlansWithFeatures();
  const requests = usePlanChangeRequests();
  const paymentConfig = usePaymentConfig();
  const [upgradeTarget, setUpgradeTarget] = useState<PlanWithFeatures | null>(null);
  const [payTarget, setPayTarget] = useState<PlanWithFeatures | null>(null);

  const canRequestUpgrade = hasPermission('billing.manage');
  const currentPlanId = subscription.data?.planId;
  const currentFee = subscription.data?.monthlyFeeAmount ?? 0;
  // Mirrors the server rule: while paying (ACTIVE/PAST_DUE) only a dearer plan;
  // otherwise (trial, expired) any self-serve plan, the current one included.
  const paying = subscription.data?.status === 'ACTIVE' || subscription.data?.status === 'PAST_DUE';
  const canPayOnline =
    canRequestUpgrade && paymentConfig.data?.onlinePayments === true && !!subscription.data;
  function payActionFor(p: PlanWithFeatures): 'upgrade' | 'activate' | null {
    if (!canPayOnline || p.isCustomPricing || p.monthlyFeeAmount <= 0) return null;
    if (p.id === currentPlanId) return paying ? null : 'activate';
    if (paying && p.monthlyFeeAmount <= currentFee) return null;
    return 'upgrade';
  }

  return (
    <div style={{ display: 'grid', gap: 'var(--space-4)', maxWidth: 1100 }}>
      <h1 style={{ margin: 0, fontSize: 'var(--font-size-xl)' }}>Billing</h1>

      {subscription.data ? (
        <p style={{ margin: 0 }}>
          Current plan: <strong>{subscription.data.planName}</strong>{' '}
          <span className="ne-badge">{subscription.data.status}</span>
          {subscription.data.endsAt ? (
            <>
              {' '}
              · {subscription.data.status === 'TRIALING' ? 'trial ends' : 'renews/ends'}{' '}
              {new Date(subscription.data.endsAt).toLocaleDateString()}
            </>
          ) : null}
        </p>
      ) : null}

      <Card title="Plans">
        {plans.isPending ? (
          <PageState kind="loading" message="Loading plans…" />
        ) : plans.isError ? (
          <PageState
            kind="error"
            message={plans.error.message}
            onRetry={() => void plans.refetch()}
          />
        ) : (
          <PlanGrid>
            {plans.data.map((p) => (
              <PlanTile
                key={p.id}
                plan={p}
                isCurrent={p.id === currentPlanId}
                canRequestUpgrade={canRequestUpgrade}
                onRequestUpgrade={() => setUpgradeTarget(p)}
                payAction={payActionFor(p)}
                onPay={() => setPayTarget(p)}
              />
            ))}
          </PlanGrid>
        )}
      </Card>

      <MyLimitsCard />

      {upgradeTarget ? (
        <UpgradeModal plan={upgradeTarget} onClose={() => setUpgradeTarget(null)} />
      ) : null}
      {payTarget ? (
        <PayPlanModal
          plan={payTarget}
          activating={payTarget.id === currentPlanId}
          onClose={() => setPayTarget(null)}
        />
      ) : null}

      {canRequestUpgrade ? (
        <Card title="Your upgrade requests">
          {requests.isPending ? (
            <PageState kind="loading" message="Loading…" />
          ) : requests.isError ? (
            <PageState kind="error" message={requests.error.message} />
          ) : requests.data.length === 0 ? (
            <PageState kind="empty" title="No requests yet" />
          ) : (
            <ul
              style={{
                margin: 0,
                padding: 0,
                listStyle: 'none',
                display: 'grid',
                gap: 'var(--space-2)',
              }}
            >
              {requests.data.map((r) => (
                <li
                  key={r.id}
                  style={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    flexWrap: 'wrap',
                    gap: 'var(--space-2)',
                    borderBottom: '1px solid var(--color-border)',
                    paddingBottom: 'var(--space-2)',
                  }}
                >
                  <span>
                    {r.targetPlanName} · {when(r.createdAt)}
                  </span>
                  <span className="ne-badge">{r.status}</span>
                </li>
              ))}
            </ul>
          )}
        </Card>
      ) : null}

      <Card title="AI quotation document packs">
        <p style={{ margin: '0 0 var(--space-2)', color: 'var(--color-text-muted)' }}>
          Need more AI-generated quotation documents than your plan includes? Top up with a pack.
          Contact us to purchase — online checkout is coming soon.
        </p>
        <ul
          style={{
            margin: 0,
            padding: 0,
            listStyle: 'none',
            display: 'grid',
            gap: 'var(--space-1)',
          }}
        >
          {AI_DOC_PACKS.map((pack) => (
            <li
              key={pack.docs}
              style={{ display: 'flex', justifyContent: 'space-between', maxWidth: 320 }}
            >
              <span>{pack.docs} documents</span>
              <strong>{pack.price}</strong>
            </li>
          ))}
        </ul>
      </Card>

      <Card title="Add-ons">
        <p style={{ margin: '0 0 var(--space-2)', color: 'var(--color-text-muted)' }}>
          One-off and recurring add-ons. Contact us to purchase — online checkout is coming soon.
        </p>
        <ul
          style={{
            margin: 0,
            padding: 0,
            listStyle: 'none',
            display: 'grid',
            gap: 'var(--space-1)',
          }}
        >
          {ONE_OFF_ADD_ONS.map((addOn) => (
            <li
              key={addOn.label}
              style={{ display: 'flex', justifyContent: 'space-between', maxWidth: 420 }}
            >
              <span>{addOn.label}</span>
              <strong>{addOn.price}</strong>
            </li>
          ))}
        </ul>
      </Card>

      <InvoicesCard />

      <p style={{ margin: 0, fontSize: 'var(--font-size-sm)' }}>
        <Link to="/">← Back to dashboard</Link>
      </p>
    </div>
  );
}
